import type { EChartsCoreOption, EChartsType } from "echarts/core";
import { init } from "echarts/core";
import mergeRefs from "merge-refs";
import { forwardRef, useEffect, useRef } from "react";
import { useMount, useUnmount, useUpdateEffect } from "react-use";

import { registerEChartsModules } from "metabase/visualizations/echarts";
import type {
  EChartsEventHandler,
  ZREventHandler,
} from "metabase/visualizations/types/echarts";

import { EChartsRendererRoot } from "./EChartsRenderer.styled";

registerEChartsModules();

export interface EChartsRendererProps {
  option: EChartsCoreOption;
  eventHandlers?: EChartsEventHandler[];
  zrEventHandlers?: ZREventHandler[];
  width: number | "auto";
  height: number | "auto";
  onInit?: (chart: EChartsType) => void;
  notMerge?: boolean;
}
function fixEChartsTimeAxis(option:any) {
  if (option?.xAxis?.type === 'time' && option?.dataset?.[0]?.source) {
    const source = option.dataset[0].source;
    const xKey = Object.keys(source[0]).find(k => k.includes('_x'));
    const times = source.map(d => new Date(d[xKey]).getTime());
    const min = Math.min(...times);
    const max = Math.max(...times);
    const span = option.xAxis?.max && option.xAxis?.min ? option.xAxis.max - option.xAxis.min : 0;

    // ✅ 仅当「只有一个数据点」且「min/max跨度≥1年」时才修复
    const ONE_YEAR = 365 * 24 * 3600 * 1000;
    const isSinglePoint = times.length === 1;
    const isOverOneYear = span >= ONE_YEAR;

    if (isSinglePoint && isOverOneYear) {
      // 缩小范围 ±1天，避免双刻度
      option.xAxis.min = min - 24 * 3600 * 1000;
      option.xAxis.max = max + 24 * 3600 * 1000;
    }
  }

  return option;
}

export const EChartsRenderer = forwardRef<HTMLDivElement, EChartsRendererProps>(
  function EChartsRenderer(
    {
      option,
      eventHandlers,
      zrEventHandlers,
      width,
      height,
      onInit,
      notMerge = true,
    }: EChartsRendererProps,
    ref,
  ) {
    option = fixEChartsTimeAxis(option);
    const chartElemRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<EChartsType>();

    useMount(() => {
      chartRef.current = init(chartElemRef.current, null, {
        width,
        height,
        renderer: "svg",
      });

      chartRef.current?.setOption(option, notMerge);
      onInit?.(chartRef.current);
    });

    useUnmount(() => {
      chartRef.current?.dispose();
    });

    useUpdateEffect(() => {
      chartRef.current?.setOption(option, notMerge);
    }, [option]);

    useUpdateEffect(() => {
      chartRef.current?.resize({ width, height });
    }, [width, height]);

    useEffect(() => {
      eventHandlers?.forEach(h => {
        if (h.query) {
          chartRef.current?.on(h.eventName, h.query, h.handler);
          return;
        }
        chartRef.current?.on(h.eventName, h.handler);
      });

      return () =>
        eventHandlers?.forEach(h =>
          chartRef.current?.off(h.eventName, h.handler),
        );
    }, [eventHandlers]);

    useEffect(() => {
      zrEventHandlers?.forEach(h => {
        chartRef.current?.getZr().on(h.eventName, h.handler);
      });

      return () =>
        zrEventHandlers?.forEach(h =>
          chartRef.current?.getZr().off(h.eventName, h.handler),
        );
    }, [zrEventHandlers]);

    return (
      <EChartsRendererRoot
        data-testid="chart-container"
        ref={mergeRefs<HTMLDivElement>(chartElemRef, ref)}
      />
    );
  },
);
